import { createSlice } from "@reduxjs/toolkit";
import {
  createErrorReportingAsyncThunk,
  serverRejectionMessage,
} from "../../helpers";
import type { Dictionary } from "lodash";
import axios, { getData } from "../../../axios";
import {
  BaseFraction,
  Fraction,
  decodeFraction,
  decodeFractions,
} from "./types";
import keyBy from "lodash/keyBy";
import { notifyL } from "../../notifications";
import { DUPLICATE_FRACTION_NAME } from "dora-shared";

const prefix = "data/fractions";

type State = {
  entities: Dictionary<Fraction>;
  ids: string[];
  loading: boolean;
};
const initialState: State = { entities: {}, ids: [], loading: false };

export const loadFractions = createErrorReportingAsyncThunk(
  `${prefix}/load`,
  async (): Promise<Fraction[]> => {
    return await axios
      .get("/api/fractions")
      .then(getData)
      .then(decodeFractions);
  }
);

// The name is unique per org, so the server answers 409
// DUPLICATE_FRACTION_NAME when it is taken. That is a refusal aimed at the
// user: it gets its own translated toast and becomes the rejection value,
// which leaves the dialog open. Everything else is rethrown untouched -
// createErrorReportingAsyncThunk is what reports it (Sentry, reportError, the
// generic toast), and it classifies the failure first, so a dropped connection
// or a 401 is not turned into "unexpected error" on the way past.
export const addFraction = createErrorReportingAsyncThunk<
  Fraction,
  BaseFraction,
  { rejectValue: string }
>(`${prefix}/add`, async (values, thunkApi) => {
  try {
    const fraction = await axios
      .post("/api/fractions", values)
      .then(getData)
      .then(decodeFraction);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "fractionSaved",
        type: "success",
      })
    );
    return fraction;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_FRACTION_NAME
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingFractionDuplicateName",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_FRACTION_NAME);
    }
    throw err;
  }
});

// Same contract as add: renaming a fraction onto a name that is taken is the
// only refusal this endpoint has of its own.
export const updateFraction = createErrorReportingAsyncThunk<
  Fraction,
  Fraction,
  { rejectValue: string }
>(`${prefix}/update`, async (fraction, thunkApi) => {
  try {
    const updated = await axios
      .put(`/api/fractions/${fraction.id}`, fraction)
      .then(getData)
      .then(decodeFraction);
    thunkApi.dispatch(
      notifyL({
        namespace: "notifications",
        key: "fractionSaved",
        type: "success",
      })
    );
    return updated;
  } catch (err) {
    const rejection = serverRejectionMessage(err);
    if (
      rejection?.kind === "code" &&
      rejection.code === DUPLICATE_FRACTION_NAME
    ) {
      thunkApi.dispatch(
        notifyL({
          namespace: "notifications",
          key: "errorSavingFractionDuplicateName",
          type: "error",
        })
      );
      return thunkApi.rejectWithValue(DUPLICATE_FRACTION_NAME);
    }
    throw err;
  }
});

export const archiveFraction = createErrorReportingAsyncThunk(
  `${prefix}/archive`,
  async (id: string) => {
    await axios.post(`/api/fractions/${id}/archive`);
  }
);

const slice = createSlice({
  name: prefix,
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(loadFractions.pending, (state) => {
        state.loading = true;
      })
      .addCase(loadFractions.rejected, (state) => {
        state.loading = false;
      })
      .addCase(loadFractions.fulfilled, (state, action) => {
        state.loading = false;
        state.entities = keyBy(action.payload, "id");
        state.ids = action.payload.map((x) => x.id);
      })
      .addCase(addFraction.fulfilled, (state, action) => {
        state.ids.unshift(action.payload.id);
        state.entities[action.payload.id] = action.payload;
      })
      .addCase(updateFraction.fulfilled, (state, action) => {
        state.entities[action.payload.id] = action.payload;
      })
      .addCase(archiveFraction.fulfilled, (state, action) => {
        const id = action.meta.arg;
        delete state.entities[id];
        state.ids = state.ids.filter((x) => x !== id);
      });
  },
});

export default slice.reducer;
